import { HttpStatusCode, isAxiosError } from "axios";
import { useApiClient } from "@/hooks/useApiClient";
import type {
  ChibanInputRow,
  CreateKaokuChibanSearchRequestResponse,
  SearchResultRow,
  ChibanInput,
  ChibanToKaokuNumberSearchParam,
  KaokuChibanSearchRequest,
} from "@/types/kaokuNumber";
import { type PropertySelectionRow } from "@/features/map/types";
import { type GridSelectionModel } from "@mui/x-data-grid-pro";
import { type BookTypeEng } from "@/types/acquirebook";
import { useState } from "react";
import { SendToTrackingService } from "@/api/handlers/errors";
import { NYFillWidthNumberToHalfWidthNumber } from "@/utils/utils";

interface KaokuNumberApi {
  convertResultToKaokuNumbers: (
    response: KaokuChibanSearchRequest
  ) => SearchResultRow[];
  convertToPropertySelectionRows: () => PropertySelectionRow[];
  createKaokuNumberSearchRequest: (
    params: ChibanToKaokuNumberSearchParam[]
  ) => Promise<CreateKaokuChibanSearchRequestResponse | undefined>;
  getKaokuNumberMaxRequestCount: () => number;
  getKaokuNumberSearchRequest: (
    id: number
  ) => Promise<KaokuChibanSearchRequest | undefined>;
  chibanInputRows: ChibanInputRow[];
  setChibanInputRows: React.Dispatch<React.SetStateAction<ChibanInputRow[]>>;
  setHitCountsFromResponse: (response: KaokuChibanSearchRequest) => void;
  kaokuNumberRows: SearchResultRow[];
  setKaokuNumberRows: React.Dispatch<React.SetStateAction<SearchResultRow[]>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  selectedChibanRowIds: GridSelectionModel;
  setSelectedChibanRowIds: React.Dispatch<
    React.SetStateAction<GridSelectionModel>
  >;
  selectedKaokuNumberRowIds: GridSelectionModel;
  setSelectedKaokuNumberRowIds: React.Dispatch<
    React.SetStateAction<GridSelectionModel>
  >;
  openSearchModalFlag: boolean;
  setOpenSearchModalFlag: React.Dispatch<React.SetStateAction<boolean>>;
  openAddRowsModalFlag: boolean;
  setOpenAddRowsModalFlag: React.Dispatch<React.SetStateAction<boolean>>;
  pollingRequestId: number | null;
  setPollingRequestId: React.Dispatch<React.SetStateAction<number | null>>;
}

/**
 * 家屋番号検索で使用するHooks及びState群（双方向対応）
 * @param {UseKaokuNumberConfig} config
 * @returns {KaokuNumberApi}
 */
export const useKaokuNumber = (searchData: ChibanInput[]): KaokuNumberApi => {
  const chibanList = searchData;
  const { apiClient } = useApiClient();

  /**
   * 家屋番号検索を依頼するAPIを実行する。
   * @param {ChibanToKaokuNumberSearchParam[]} requests
   * @returns {Promise<CreateKaokuChibanSearchRequestResponse | undefined>}
   */
  const createKaokuNumberSearchRequest = async (
    requests: ChibanToKaokuNumberSearchParam[]
  ): Promise<CreateKaokuChibanSearchRequestResponse | undefined> => {
    try {
      const response =
        await apiClient.post<CreateKaokuChibanSearchRequestResponse>(
          "/kaoku-number/",
          { requests }
        );

      return { ...response.data, status: response.status };
    } catch (error) {
      if (isAxiosError<CreateKaokuChibanSearchRequestResponse>(error)) {
        if (
          (error.response?.status ?? HttpStatusCode.InternalServerError) >=
          HttpStatusCode.InternalServerError
        ) {
          SendToTrackingService(error);
        }
        // Unauthorized以外のエラーが発生した場合、ステータスコードを返す
        if (error.response?.status !== HttpStatusCode.Unauthorized) {
          return {
            status:
              error.response?.status ?? HttpStatusCode.InternalServerError, // ステータスコードが取得できない場合は500を返す
            error: error.response?.data?.error,
          };
        }
      }

      // その他のエラーの場合はundefinedを返す
      return undefined;
    }
  };

  /**
   * 家屋番号検索リクエストの詳細を取得するAPIを実行する。
   * @param {number} id
   * @returns {Promise<KaokuChibanSearchRequest | undefined>}
   */
  const getKaokuNumberSearchRequest = async (
    id: number
  ): Promise<KaokuChibanSearchRequest | undefined> => {
    const response = await apiClient
      .get<KaokuChibanSearchRequest>(`/kaoku-number/${id}`)
      .catch((error): void => {
        if (
          isAxiosError(error) &&
          (error.response?.status ?? HttpStatusCode.InternalServerError) >=
            HttpStatusCode.InternalServerError
        ) {
          SendToTrackingService(error);
        }

        return undefined;
      });

    if (response) {
      return response.status === HttpStatusCode.Ok ? response.data : undefined;
    }
  };

  /**
   * 環境変数からVITE_KAOKU_NUMBER_MAX_REQUEST_COUNTを読み込んで数値型で返す。
   * 非数の場合はエラーを投げる。
   * @returns {number}
   */
  const getKaokuNumberMaxRequestCount = (): number => {
    const maxRequestCount = Number(
      process.env.VITE_KAOKU_NUMBER_MAX_REQUEST_COUNT
    );
    if (isNaN(maxRequestCount))
      throw new Error(
        "環境変数にVITE_KAOKU_NUMBER_MAX_REQUEST_COUNTが設定されていません。"
      );

    return maxRequestCount;
  };

  // 入力側: 地番入力行（bookTypeはUI行には含めない）
  const [chibanInputRows, setChibanInputRows] = useState<ChibanInputRow[]>(() =>
    chibanList.map((row, index) => ({
      id: index,
      selected: true,
      prefectureName: row.prefectureName,
      location: row.location,
      chiban: row.chiban,
    }))
  );

  /**
   * KaokuChibanSearchRequestをSearchResultRow[]に変換する。
   * @param {KaokuChibanSearchRequest} response
   * @returns {SearchResultRow[]}
   */
  const convertResultToKaokuNumbers = (
    response: KaokuChibanSearchRequest
  ): SearchResultRow[] => {
    const kaokuNumberList: SearchResultRow[] = [];

    if (response.responses) {
      response.responses.results.forEach((result) => {
        result.kaokuNumberList.forEach((kaokuNumber) => {
          kaokuNumberList.push({
            id: kaokuNumberList.length,
            selected: true,
            prefectureName: result.pref,
            location: result.shozai,
            chiban: result.chiban,
            kaokuNumber,
            chibanFull: `${result.pref}${result.shozai}${result.chiban}`,
          });
        });
      });
    }

    return kaokuNumberList;
  };

  /**
   * SearchResultRow[]をPropertySelectionRow[]に変換する。
   * @returns {PropertySelectionRow[]}
   */
  const convertToPropertySelectionRows = (): PropertySelectionRow[] => {
    return kaokuNumberRows
      .filter((row) => {
        return selectedKaokuNumberRowIds.includes(row.id);
      })
      .map((row, index) => {
        return {
          id: index,
          bookType: "BUILDING" as BookTypeEng,
          selected: true,
          prefName: row.prefectureName,
          location: row.location,
          chibanKaokuNumber: row.kaokuNumber,
          area: null,
          isFeedOrigin: false,
        };
      });
  };

  // ローディングのState
  const [loading, setLoading] = useState<boolean>(false);
  // 家屋番号領域に表示するリストのState
  const [kaokuNumberRows, setKaokuNumberRows] = useState<SearchResultRow[]>([]);
  // 家屋番号領域で選択された行を保持するState
  const [selectedKaokuNumberRowIds, setSelectedKaokuNumberRowIds] =
    useState<GridSelectionModel>([]);
  // 地番領域で選択された行を保持するState
  const [selectedChibanRowIds, setSelectedChibanRowIds] =
    useState<GridSelectionModel>(() => chibanList.map((_, index) => index));
  // 地番領域のボタン押下時確認ダイアログ表示State
  const [openSearchModalFlag, setOpenSearchModalFlag] =
    useState<boolean>(false);
  // 家屋番号領域のボタン押下時確認ダイアログ表示State
  const [openAddRowsModalFlag, setOpenAddRowsModalFlag] =
    useState<boolean>(false);
  // ポーリングしているリクエストIDのState
  const [pollingRequestId, setPollingRequestId] = useState<number | null>(null);

  /**
   * 検索レスポンスから地番ごとのヒット件数を集計して入力行に設定する
   * @param {KaokuChibanSearchRequest} response - 家屋番号検索のレスポンス
   */
  const setHitCountsFromResponse = (
    response: KaokuChibanSearchRequest
  ): void => {
    if (!response.responses) return;

    const hitCountMap = new Map<string, number>();
    response.responses.results.forEach((result) => {
      // 入力データ（全角）とレスポンスデータ（半角）のキーを一致させるため、
      // 両方を半角に正規化してマッチングを行う
      const key = NYFillWidthNumberToHalfWidthNumber(
        `${result.pref}${result.shozai}${result.chiban}`
      );
      hitCountMap.set(key, result.kaokuNumberList.length);
    });

    setChibanInputRows((prevRows) =>
      prevRows.map((row) => {
        // チェックボックスが外されている行（検索対象外）はundefinedのまま
        if (!selectedChibanRowIds.includes(row.id)) {
          return { ...row, hitCount: undefined };
        }

        // 入力行のキーも半角に正規化して、レスポンスのキーと一致させる
        const key = NYFillWidthNumberToHalfWidthNumber(
          `${row.prefectureName}${row.location}${row.chiban}`
        );
        return {
          ...row,
          hitCount: hitCountMap.get(key) ?? 0,
        };
      })
    );
  };

  return {
    convertResultToKaokuNumbers,
    chibanInputRows,
    setChibanInputRows,
    setHitCountsFromResponse,
    convertToPropertySelectionRows,
    createKaokuNumberSearchRequest,
    getKaokuNumberMaxRequestCount,
    getKaokuNumberSearchRequest,
    kaokuNumberRows,
    setKaokuNumberRows,
    loading,
    setLoading,
    selectedChibanRowIds,
    setSelectedChibanRowIds,
    selectedKaokuNumberRowIds,
    setSelectedKaokuNumberRowIds,
    openSearchModalFlag,
    setOpenSearchModalFlag,
    openAddRowsModalFlag,
    setOpenAddRowsModalFlag,
    pollingRequestId,
    setPollingRequestId,
  };
};
